import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/Button.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Field } from "../../components/ui/Field.js";
import { apiDelete, apiGet, apiPost } from "../../lib/api.js";
import { useMe } from "../../lib/auth.js";
import { ErrorPanel, LoadingPanel, MutationError } from "../shared.js";

type EmployeeTarget = {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  status: string;
};

type ApiKey = {
  id: string;
  name: string;
  prefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
};

export function SystemAdminPanel() {
  const client = useQueryClient();
  const me = useMe();
  const [name, setName] = useState("");
  const [secret, setSecret] = useState<string | null>(null);
  const employees = useQuery({
    queryKey: ["employees", "system-admin-targets"],
    queryFn: () =>
      apiGet<{ data: EmployeeTarget[] }>("/employees", {
        query: { status: "active" },
      }).then((response) => response.data),
  });
  const keys = useQuery({
    queryKey: ["system-admin", "api-keys"],
    queryFn: () =>
      apiGet<{ data: ApiKey[] }>("/system-admin/api-keys").then(
        (response) => response.data,
      ),
  });
  const create = useMutation({
    mutationFn: () =>
      apiPost<{ data: ApiKey & { token: string } }>("/system-admin/api-keys", {
        name,
      }),
    onSuccess: async (response) => {
      setSecret(response.data.token);
      setName("");
      await client.invalidateQueries({
        queryKey: ["system-admin", "api-keys"],
      });
    },
  });
  const impersonate = useMutation({
    mutationFn: (id: string) => apiPost(`/system-admin/impersonate/${id}`),
    onSuccess: async () => {
      await me.refetch();
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => apiDelete(`/system-admin/api-keys/${id}`),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: ["system-admin", "api-keys"] }),
  });
  if (keys.isLoading) return <LoadingPanel />;
  if (keys.error) return <ErrorPanel error={keys.error} />;
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-app-text">
          System administrator
        </h2>
        <p className="mt-1 text-sm text-app-muted">
          Manage full-access API keys for the hidden administrator account.
        </p>
      </div>
      <div className="rounded-xl border border-app-border bg-app-surface p-4">
        <h3 className="font-semibold text-app-text">Employee access</h3>
        <p className="mt-1 text-sm text-app-muted">
          Impersonate an employee to verify their experience. The session is
          audited and can be exited from the banner.
        </p>
        <div className="mt-3 divide-y divide-app-border">
          {employees.data?.map((employee) => (
            <div
              key={employee.id}
              className="flex flex-wrap items-center justify-between gap-3 py-3"
            >
              <div>
                <div className="font-medium text-app-text">
                  {employee.firstName} {employee.lastName}
                </div>
                <div className="text-sm text-app-muted">
                  {employee.email ?? "No email"}
                </div>
              </div>
              <Button
                type="button"
                variant="outlined"
                severity="secondary"
                loading={
                  (impersonate.isPending &&
                    impersonate.variables === employee.id) ||
                  undefined
                }
                onClick={() => impersonate.mutate(employee.id)}
              >
                Impersonate
              </Button>
            </div>
          ))}
          {!employees.isLoading && !employees.data?.length ? (
            <p className="py-3 text-sm text-app-muted">
              No active employees available.
            </p>
          ) : null}
        </div>
      </div>
      <MutationError error={impersonate.error} />
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Field label="API key name" className="min-w-64 flex-1">
          <Input
            value={name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setName(event.target.value)
            }
            required
          />
        </Field>
        <Button type="submit" loading={create.isPending || undefined}>
          Create API key
        </Button>
      </form>
      <MutationError error={create.error || revoke.error} />
      {secret ? (
        <div className="rounded-lg border border-warning-border bg-warning-soft p-4 text-sm">
          <strong>Copy this API key now.</strong> It will not be shown again.
          <code className="mt-2 block break-all rounded bg-app-surface p-2 text-app-text">
            {secret}
          </code>
          <Button
            type="button"
            size="small"
            variant="text"
            onClick={() => setSecret(null)}
          >
            Dismiss
          </Button>
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b text-left text-app-muted">
              <th className="p-2">Name</th>
              <th className="p-2">Prefix</th>
              <th className="p-2">Last used</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {keys.data?.map((key) => (
              <tr key={key.id} className="border-b border-app-border">
                <td className="p-2">{key.name}</td>
                <td className="p-2 font-mono">{key.prefix}…</td>
                <td className="p-2">
                  {key.lastUsedAt
                    ? new Date(key.lastUsedAt).toLocaleString()
                    : "Never"}
                </td>
                <td className="p-2 text-right">
                  <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    severity="danger"
                    disabled={Boolean(key.revokedAt)}
                    onClick={() => revoke.mutate(key.id)}
                  >
                    Revoke
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
